<template>
  <div class="download-multiselect-container">
    <div class="custom-dropdown">
      <button
        type="button"
        class="btn btn-outline-secondary btn-sm download-button"
        @click.stop="toggleDropdown"
        :disabled="loading || disabled"
        :title="title"
      >
        {{ buttonText }}   {{ isOpen ? '↑' : '↓' }}
      </button>
      
      <div class="custom-dropdown-menu" :class="{ show: isOpen }">
        <button
          class="custom-dropdown-item"
          @click="downloadReport"
          :disabled="loading"
        >
          Скачать отчет
        </button>
        <button
          class="custom-dropdown-item"
          @click="downloadChart"
          :disabled="loading"
        >
          Скачать график
        </button>
        <button
          class="custom-dropdown-item"
          @click="copyChart"
          :disabled="loading"
        >
          Скопировать график
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import ChartAPI from "./chart-api";
import {mapGetters} from "vuex";
import html2canvas from 'html2canvas';

export default {
  name: "DownloadMultiselectButton",
  props: {
    downloadReportType: {
      type: String,
      default: "download-sov"
    },
    urls: {
      type: Array,
      default: []
    },
    disabled: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ""
    },
    chartContainerSelector: {
      type: String,
      required: true
    },
    chartType: {
      type: String,
      default: "sov"
    }
  },

  data() {
    return {
      studObj: new ChartAPI(),
      loading: false,
      isOpen: false
    };
  },

  computed: {
    buttonText: function () {
      return "Скачать";
    },
    ...mapGetters(["currentProjectId"]),
  },

  mounted() {
    // Закрываем dropdown при клике вне его
    document.addEventListener('click', this.handleDocumentClick);
  },

  beforeDestroy() {
    document.removeEventListener('click', this.handleDocumentClick);
  },

  methods: {
    toggleDropdown() {
      this.isOpen = !this.isOpen;
    },

    handleDocumentClick(event) {
      // Проверяем, что клик был вне нашего компонента
      if (!this.$el.contains(event.target)) {
        this.isOpen = false;
      }
    },

    closeDropdown() {
      this.isOpen = false;
    },

    closeDropdownInstantly() {
      this.isOpen = false;
    },

    async downloadReport() {
      this.loading = true;
      this.isOpen = false;
      
      try {
        let studObj = new ChartAPI();
        await studObj.getDownloadReport(this.downloadReportType, this.urls, this.currentProjectId);
      } catch (error) {
        console.error('Ошибка при скачивании отчета:', error);
      } finally {
        this.loading = false;
      }
    },

    async downloadChart() {
      this.loading = true;
      this.closeDropdownInstantly();
      
      try {
        // Небольшая задержка чтобы dropdown успел закрыться
        await new Promise(resolve => setTimeout(resolve, 100));
        await this.takeScreenshot(false); // false = скачать файл
      } catch (error) {
        console.error('Ошибка при скачивании графика:', error);
        alert('Ошибка при скачивании графика: ' + error.message);
      } finally {
        this.loading = false;
      }
    },

    async copyChart() {
      this.loading = true;
      this.closeDropdownInstantly();
      
      try {
        // Небольшая задержка чтобы dropdown успел закрыться
        await new Promise(resolve => setTimeout(resolve, 100));
        await this.takeScreenshot(true); // true = скопировать в буфер
        alert('График скопирован в буфер обмена');
      } catch (error) {
        console.error('Ошибка при копировании графика:', error);
        alert('Ошибка при копировании графика: ' + error.message);
      } finally {
        this.loading = false;
      }
    },

    async takeScreenshot(copyToClipboard = false) {
      return new Promise((resolve, reject) => {
        try {
          // Находим контейнер с графиком
          const container = document.querySelector(this.chartContainerSelector);
          if (!container) {
            reject(new Error('Контейнер графика не найден'));
            return;
          }

          // Используем html2canvas для создания скриншота
          html2canvas(container, {
            backgroundColor: '#ffffff',
            scale: 2, // Увеличиваем разрешение
            useCORS: true,
            allowTaint: true,
            logging: false
          }).then(canvas => {
            if (copyToClipboard) {
              // Копируем в буфер обмена
              canvas.toBlob(blob => {
                if (navigator.clipboard && navigator.clipboard.write) {
                  navigator.clipboard.write([
                    new ClipboardItem({
                      'image/png': blob
                    })
                  ]).then(() => {
                    resolve();
                  }).catch(err => {
                    reject(err);
                  });
                } else {
                  reject(new Error('Буфер обмена не поддерживается'));
                }
              }, 'image/png');
            } else {
              // Скачиваем файл
              const link = document.createElement('a');
              link.download = this.generateFileName();
              link.href = canvas.toDataURL('image/png');
              link.click();
              resolve();
            }
          }).catch(err => {
            reject(err);
          });
        } catch (error) {
          reject(error);
        }
      });
    },

    generateFileName() {
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0];
      return `graph_${this.chartType}_${dateStr}.png`;
    }
  }
};
</script>

<style scoped>
.download-multiselect-container {
  position: relative;
  display: inline-block;
}

.custom-dropdown {
  position: relative;
  display: inline-block;
}

.custom-dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 1000;
  min-width: 160px;
  padding: 0.5rem 0;
  margin: 0.125rem 0 0;
  background-color: #fff;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 0.375rem;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.175);
  opacity: 0;
  transform: translateY(-10px);
  transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out;
  pointer-events: none;
}

.custom-dropdown-menu.show {
  opacity: 1;
  transform: translateY(0);
  pointer-events: auto;
}

.custom-dropdown-item {
  display: block;
  width: 100%;
  padding: 0.375rem 0.75rem;
  clear: both;
  font-weight: 400;
  color: #ffffff;
  text-align: left;
  text-decoration: none;
  white-space: nowrap;
  background-color: #3d46a1;
  border: 1px solid #3d46a1;
  border-radius: 0.25rem;
  margin: 0.125rem;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  font-size: 0.875rem;
}

.custom-dropdown-item:hover {
  color: #ffffff;
  background-color: #2d3571;
  border-color: #2d3571;
}

.custom-dropdown-item:disabled {
  color: #6c757d;
  background-color: #e9ecef;
  border-color: #e9ecef;
  cursor: not-allowed;
}

.custom-dropdown-item:disabled:hover {
  background-color: #e9ecef;
  border-color: #e9ecef;
}

/* Адаптация под размер существующих кнопок */
.download-button {
  min-width: 80px;
}

/* Стили для кнопки "Таблица" чтобы они были одинакового размера */
.go-to-table {
  min-width: 80px;
}
</style>
